// src/screens/PlayerPage.tsx
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { ArrowLeft, CalendarDays, User2, Pencil, Share2, Copy, Check, Mail } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import i18n from "../i18n";
import { v4 as uuidv4 } from "uuid";
import { getAppBaseUrl } from "../utils/url";

/* ========= i18n mini-hook (PATCH: accepte string fallback) ========= */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);

  /** t("key", "fallback") -> si pas de traduction, rend le fallback */
  const t = (key: string, options?: Record<string, unknown> | string): string => {
    if (typeof options === "string") {
      const translated = (i18n.t as any)(key);
      if (translated === key) return options;
      return String(translated);
    }
    return String((i18n.t as any)(key, options));
  };

  return { t, lng };
}

type Player = {
  id: string;
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string;
  age?: number;
};

type TeamRow = { id: string; team_name: string; players: Player[] };

type TrainingRow = { id: string; team_id: string; user_id?: string; date?: string };
type AttendanceRow = {
  training_id: string;
  player_id?: string | null;
  player_json_id?: string | null;
  was_present: boolean | null;
};

export const PlayerPage = () => {
  const { t, lng } = useI18nSafe();

  // Routes supportées :
  // 1) /player/by-id/:playerId  ✅ (prioritaire)
  // 2) /player/:teamId/:number  (fallback)
  // 3) /player/:number          (fallback)
  const { playerId, number, teamId } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState<Player | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [stats, setStats] = useState<any[]>([]);
  const [average, setAverage] = useState<any>({});
  const [matchInfos, setMatchInfos] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [playerJsonId, setPlayerJsonId] = useState<string | null>(null);

  // Premium + présence
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [attendanceCounts, setAttendanceCounts] = useState<{ present: number; unknown: number; total: number }>({
    present: 0,
    unknown: 0,
    total: 0,
  });
  // Invitation (owner/coach/assistant only)
  // NEW: édition réservée au propriétaire
  const [canEdit, setCanEdit] = useState(false);

  // Invitation (owner/coach/assistant only)
  const [canInvite, setCanInvite] = useState(false);
  const [chosenTeamId, setChosenTeamId] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteLink, setInviteLink] = useState<string>("");
  const [inviteEmail, setInviteEmail] = useState<string>("");
  const [copied, setCopied] = useState(false);

  const PIE_COLORS = ["#22c55e", "#a1a1aa"]; // green / gray

  const attendanceChartData = useMemo(() => {
    const arr = [{ name: t("playerpage:attendance.present"), value: attendanceCounts.present }];
    if (attendanceCounts.unknown > 0) {
      arr.push({ name: t("playerpage:attendance.absent"), value: attendanceCounts.unknown });
    }
    return arr;
  }, [attendanceCounts, t]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setLoading(false);
        return;
      }

      // --- Premium ---
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();
      setIsPremium(!!profile?.is_premium);

      // --- 1) Récupérer les équipes accessibles (laisser RLS filtrer) ---
      const { data: teams, error: teamErr } = await supabase
        .from("teams")
        .select("id, team_name, players"); // ⬅️ plus de .eq("user_id", user.id)

      if (teamErr) {
        console.error("teams fetch error:", teamErr);
        setLoading(false);
        return;
      }

      let chosenTeam: TeamRow | null = null;
      let foundPlayer: Player | null = null;

      // --- 1a) Recherche par playerId (id JSONB) ---
      if (playerId) {
        for (const tTeam of teams || []) {
          const p =
            (tTeam.players || []).find((pl: any) => String(pl.id) === String(playerId)) || null;
          if (p) {
            foundPlayer = p as Player;
            chosenTeam = tTeam as TeamRow;
            break;
          }
        }
      }

      // --- 1b) Sinon, recherche par numéro (ancien comportement) ---
      if (!foundPlayer) {
        const jersey = Number(number);
        if (teamId) {
          chosenTeam = (teams || []).find((t) => t.id === teamId) || null;
          foundPlayer =
            (chosenTeam?.players || []).find((p: any) => Number(p.number) === jersey) || null;
        }
        if (!foundPlayer && !playerId && number) {
          for (const tTeam of teams || []) {
            const candidate =
              (tTeam.players || []).find((p: any) => Number(p.number) === jersey) || null;
            if (candidate) {
              foundPlayer = candidate as Player;
              chosenTeam = tTeam as TeamRow;
              break;
            }
          }
        }
      }

      const localJsonId = foundPlayer ? String(foundPlayer.id) : null;
      setPlayer(foundPlayer);
      setPlayerJsonId(localJsonId);
      setChosenTeamId(chosenTeam?.id ?? null);

      // URL signée si photo
      if (foundPlayer?.photoUrl) {
        const { data: signed } = await supabase.storage
          .from("player-photos")
          .createSignedUrl(foundPlayer.photoUrl, 3600);
        setSignedUrl(signed?.signedUrl ?? null);
      } else {
        setSignedUrl(null);
      }

      // --- 2) Récupérer les matchs (laisser RLS filtrer) ---
      let matchQuery = supabase
        .from("matches")
        .select("id, date, created_at, opponent, team_id"); // ⬅️ plus de .eq("user_id", user.id)

      if (chosenTeam?.id) {
        matchQuery = matchQuery.eq("team_id", chosenTeam.id);
      }

      const { data: matchList, error: matchErr } = await matchQuery;
      if (matchErr) {
        console.error("matches fetch error:", matchErr);
        setStats([]);
        setAverage({});
        setMatchInfos({});
        setLoading(false);
        return;
      }

      const matchIds = matchList?.map((m) => m.id) || [];
      const matchMap: Record<string, any> = {};
      matchList?.forEach((m) => (matchMap[m.id] = m));
      setMatchInfos(matchMap);

      // --- 3) Stats du joueur ---
      if (matchIds.length > 0) {
        const { data: matchStats, error: statsErr } = await supabase
          .from("match_stats")
          .select("*")
          .in("match_id", matchIds);

        if (statsErr) {
          console.error("match_stats fetch error:", statsErr);
          setStats([]);
          setAverage({});
        } else {
          const jersey = foundPlayer ? Number(foundPlayer.number) : Number(number);
          const fullName = foundPlayer
            ? `${foundPlayer.firstName} ${foundPlayer.lastName}`.toLowerCase()
            : null;

          const playerStats = (matchStats || []).filter(
            (s) =>
              (localJsonId && s.player_json_id === localJsonId) ||
              Number(s.player_number) === jersey ||
              (fullName && s.player_name?.toLowerCase() === fullName)
          );

          const sorted = [...playerStats].sort((a, b) => {
            const da = new Date(
              matchMap[a.match_id]?.date || matchMap[a.match_id]?.created_at || 0
            ).getTime();
            const db = new Date(
              matchMap[b.match_id]?.date || matchMap[b.match_id]?.created_at || 0
            ).getTime();
            return db - da;
          });

          setStats(sorted);

          if (playerStats.length > 0) {
            const total = playerStats.reduce((acc: any, stat: any) => {
              Object.keys(stat).forEach((k) => {
                if (typeof stat[k] === "number") {
                  acc[k] = (acc[k] || 0) + stat[k];
                }
              });
              return acc;
            }, {});

            const avg = Object.fromEntries(
              Object.entries(total).map(([k, v]) => [k, (v as number / playerStats.length).toFixed(1)])
            );
            setAverage(avg);
          } else {
            setAverage({});
          }
        }
      } else {
        setStats([]);
        setAverage({});
      }

      // --- 4) Présence aux entraînements (laisser RLS filtrer) ---
      if (chosenTeam?.id && foundPlayer?.id) {
        const { data: trainings, error: trErr } = await supabase
          .from("trainings")
          .select("id, team_id, date") // ⬅️ plus de user_id
          .eq("team_id", chosenTeam.id);

        if (!trErr && (trainings?.length || 0) > 0) {
          const now = new Date();
          // On ne compte que les entraînements déjà passés : un entraînement futur n'a pas
          // encore de présence à évaluer et ne doit pas compter comme "absent"/"inconnu".
          const pastTrainingIds = (trainings as TrainingRow[])
            .filter((tr) => !tr.date || new Date(tr.date) <= now)
            .map((tr) => tr.id);
          let present = 0;
          let absent = 0;

          if (pastTrainingIds.length > 0) {
            const playerIdStr = String(foundPlayer.id);
            const { data: attRows, error: attErr } = await supabase
              .from("training_attendance")
              .select("training_id, player_id, player_json_id, was_present")
              .in("training_id", pastTrainingIds)
              .or(`player_json_id.eq.${playerIdStr},player_id.eq.${playerIdStr}`);

            if (!attErr && attRows) {
              (attRows as AttendanceRow[]).forEach((r) => {
                if (r.was_present) present += 1;
                else absent += 1;
              });
            }
          }

          const total = pastTrainingIds.length;
          const noData = Math.max(0, total - (present + absent));
          const unknown = absent + noData;
          setAttendanceCounts({ present, unknown, total });
        } else {
          setAttendanceCounts({ present: 0, unknown: 0, total: 0 });
        }
      } else {
        setAttendanceCounts({ present: 0, unknown: 0, total: 0 });
      }

      setLoading(false);
    };

    run();
  }, [playerId, number, teamId]);

  // --- Vérifie si user courant peut inviter ce joueur ---
  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid || !chosenTeamId) {
        setCanInvite(false);
        setCanEdit(false);            // <- NEW
        return;
      }

      // Cas 1 : créateur de l’équipe
      const { data: team } = await supabase
        .from("teams")
        .select("id, user_id")
        .eq("id", chosenTeamId)
        .maybeSingle();

      if (team && (team as any).user_id === uid) {
        setCanInvite(true);
        setCanEdit(true);             // <- NEW (owner peut éditer)
        return;
      }

      // Cas 2 : membre staff (coach/assistant) -> peut inviter mais pas éditer
      const { data: member } = await supabase
        .from("team_members")
        .select("role,status")
        .eq("team_id", chosenTeamId)
        .eq("user_id", uid)
        .eq("status", "active")
        .maybeSingle();

      const role = (member as any)?.role;
      setCanInvite(["owner", "coach", "assistant"].includes(role));
      setCanEdit(false);              // <- NEW (pas owner => pas d’édition)
    })();
  }, [chosenTeamId]);


  const openInvite = async () => {
    if (!chosenTeamId || !player) return;

    const token = uuidv4().replace(/-/g, "");
    const link = `${getAppBaseUrl()}/accept-player-invite/${token}`;

    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) {
      alert("Tu dois être connecté pour inviter un joueur.");
      return;
    }

    console.log("[invite] user:", uid);

    const { data, error } = await supabase
      .from("player_portal_invites")
      .insert([
        {
          team_id: chosenTeamId,
          player_json_id: String(player.id),
          player_number: Number(player.number),
          email: inviteEmail || null,
          token,
          created_by: uid,          // 👈 important
        },
      ])
      .select("token")
      .single();

    if (error) {
      console.error("invite insert error:", error);
      alert(`Impossible de créer l’invitation: ${error.message}`);
      return;
    }

    console.log("[invite] insert ok:", data);
    setInviteLink(link);
    setShowInvite(true);
    setCopied(false);
  };


  const copyLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 900);
  };

  // --- UI ---
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 grid place-items-center">
        <p className="text-gray-600 dark:text-gray-400">{t("common:loading", "Chargement…")}</p>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100 p-6 max-w-xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("playerpage:nav.back", "Retour")}
        </button>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <p className="text-gray-700 dark:text-gray-300">
            {t("playerpage:not_found", "Joueur introuvable")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("playerpage:nav.back", "Retour")}
          </button>
        </div>

        <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Avatar */}
            <div className="shrink-0">
              {signedUrl ? (
                <img
                  src={signedUrl}
                  alt={`${player.firstName} ${player.lastName}`}
                  className="w-24 h-24 rounded-full object-cover ring-2 ring-orange-500/20"
                />
              ) : (
                <div className="w-24 h-24 rounded-full grid place-items-center bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <User2 className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>

            {/* Infos + badges */}
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold break-words">
                {player.firstName} {player.lastName}
              </h1>

              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <span className="inline-flex px-2.5 py-1 rounded-full border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  # {player.number}
                </span>
                <span className="inline-flex px-2.5 py-1 rounded-full border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  {t("playerpage:badges.position", "Poste")}: {player.position}
                </span>
                {player.age && (
                  <span className="inline-flex px-2.5 py-1 rounded-full border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    {t("playerpage:badges.age", "Âge")}: {player.age}
                  </span>
                )}
              </div>

              {/* ✅ Actions : sous les infos sur mobile, à droite sur desktop */}
              <div className="mt-6 sm:mt-0 sm:ml-auto flex flex-col gap-2 w-full sm:w-auto">
                {playerJsonId && canEdit && (
                  <button
                    onClick={() => navigate(`/editplayer/${playerJsonId}`)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg shadow whitespace-nowrap"
                    title={t("playerpage:edit_player.title", "Éditer")}
                  >
                    <Pencil className="w-4 h-4" />
                    {t("playerpage:edit_player.cta", "Modifier")}
                  </button>
                )}

                {canInvite && (
                  <button
                    onClick={() => setShowInvite(true)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 px-3 py-2 rounded-lg whitespace-nowrap"
                    title={t("playerpage:invite.title", "Inviter")}
                  >
                    <Share2 className="w-4 h-4" />
                    {t("playerpage:invite.cta", "Inviter ce joueur")}
                  </button>
                )}
              </div>
            </div>
          </div>

          <h2 className="text-xl font-semibold mt-6 mb-3">{t("playerpage:averages.title", "Moyennes")}</h2>
          {stats.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-800/80">
                  <tr className="text-left">
                    <th className="p-3">{t("playerpage:averages.cols.points", "Pts")}</th>
                    <th className="p-3">{t("playerpage:averages.cols.rebounds", "Reb")}</th>
                    <th className="p-3">{t("playerpage:averages.cols.assists", "Ast")}</th>
                    <th className="p-3">{t("playerpage:averages.cols.steals", "Stl")}</th>
                    <th className="p-3">{t("playerpage:averages.cols.fg_pct", "% Tir")}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900">
                  <tr>
                    <td className="p-3">{average.points || "0"}</td>
                    <td className="p-3">{average.rebounds || "0"}</td>
                    <td className="p-3">{average.assists || "0"}</td>
                    <td className="p-3">{average.steals || "0"}</td>
                    <td className="p-3">
                      {average.shots_attempted && Number(average.shots_attempted) > 0
                        ? `${((Number(average.shots_made) / Number(average.shots_attempted)) * 100).toFixed(1)} %`
                        : "0 %"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">
              {t("playerpage:averages.empty", "Aucune stat pour l’instant.")}
            </p>
          )}
        </section>

        {/* --- Section Premium : Camembert de présence --- */}
        {isPremium && (
          <section className="mt-8">
            <h2 className="text-xl font-semibold mb-3">{t("playerpage:attendance.title", "Présence aux entraînements")}</h2>

            {attendanceCounts.total > 0 ? (
              <div className="flex items-center gap-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5">
                <PieChart width={230} height={215}>
                  <Pie
                    data={attendanceChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={50}
                  >
                    {attendanceChartData.map((_, idx) => (
                      <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>

                <div className="text-sm">
                  <p className="mb-1">
                    {t("playerpage:attendance.total", "Total")}: <strong>{attendanceCounts.total}</strong>
                  </p>
                  <p className="mb-1">
                    {t("playerpage:attendance.present", "Présent")}: <strong>{attendanceCounts.present}</strong>
                  </p>
                  {attendanceCounts.unknown > 0 && (
                    <p className="mb-1">
                      {t("playerpage:attendance.absent", "Absent")}: <strong>{attendanceCounts.unknown}</strong>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                {t("playerpage:attendance.empty", "Aucune donnée de présence.")}
              </p>
            )}
          </section>
        )}

        <section className="mt-8">
          <h2 className="text-xl font-semibold mb-3">{t("playerpage:by_match.title", "Match par match")}</h2>
          {stats.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-800/80">
                  <tr className="text-left">
                    <th className="p-3">
                      <span className="inline-flex items-center gap-2">
                        <CalendarDays className="w-4 h-4" />
                        {t("playerpage:by_match.cols.date", "Date")}
                      </span>
                    </th>
                    <th className="p-3">{t("playerpage:by_match.cols.opponent", "Adversaire")}</th>
                    <th className="p-3">{t("playerpage:by_match.cols.points", "Pts")}</th>
                    <th className="p-3">{t("playerpage:by_match.cols.assists", "Ast")}</th>
                    <th className="p-3">{t("playerpage:by_match.cols.rebounds", "Reb")}</th>
                    <th className="p-3">{t("playerpage:by_match.cols.steals", "Stl")}</th>
                    <th className="p-3">{t("playerpage:by_match.cols.fg", "Tirs")}</th>
                    <th className="p-3">{t("playerpage:by_match.cols.fg_pct", "%")}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900">
                  {stats.map((s, idx) => {
                    const match = matchInfos[s.match_id];
                    const date = match?.date || match?.created_at || "–";
                    const dateStr = new Date(date).toLocaleDateString(lng === "fr" ? "fr-FR" : "en-US");
                    const opponent = match?.opponent || "–";
                    const percentage =
                      s.shots_attempted > 0
                        ? `${((s.shots_made / s.shots_attempted) * 100).toFixed(1)}%`
                        : "0%";

                    return (
                      <tr
                        key={idx}
                        className="border-t border-gray-200 dark:border-gray-800 hover:bg-orange-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                        onClick={() => navigate(`/matchdetails/${s.match_id}`)}
                      >
                        <td className="p-3">{dateStr}</td>
                        <td className="p-3">{opponent}</td>
                        <td className="p-3">{s.points}</td>
                        <td className="p-3">{s.assists}</td>
                        <td className="p-3">{s.rebounds}</td>
                        <td className="p-3">{s.steals}</td>
                        <td className="p-3">
                          {s.shots_made}/{s.shots_attempted}
                        </td>
                        <td className="p-3">{percentage}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">
              {t("playerpage:by_match.empty", "Pas encore de matchs.")}
            </p>
          )}
        </section>
      </div>

      {/* ---- MODALE INVITATION ---- */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setShowInvite(false);
              setInviteLink("");
            }}
          />
          <div className="relative z-10 w-full max-w-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-3">
              {t("playerpage:invite.modal_title", "Inviter ce joueur")}
            </h3>

            {!inviteLink && (
              <>
                <label className="block text-sm mb-1">
                  {t("playerpage:invite.email_optional", "Email du joueur (optionnel)")}
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="prenom@email.com"
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                  />
                  <button
                    onClick={openInvite}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700"
                  >
                    <Mail className="w-4 h-4" />
                    {t("playerpage:invite.generate_link", "Générer le lien")}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t(
                    "playerpage:invite.hint",
                    "Tu peux laisser vide et copier le lien à partager."
                  )}
                </p>
              </>
            )}

            {inviteLink && (
              <>
                <label className="block text-sm mb-1 mt-2">
                  {t("playerpage:invite.link_label", "Lien d’accès privé")}
                </label>
                <div className="flex">
                  <input
                    readOnly
                    value={inviteLink}
                    className="flex-1 rounded-l-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs"
                  />
                  <button
                    onClick={copyLink}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-r-lg border border-l-0 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                    title={t("common:copy", "Copier")}
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>

                <div className="flex justify-end mt-4 gap-2">
                  <button
                    onClick={() => {
                      setShowInvite(false);
                      setInviteLink("");
                    }}
                    className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {t("common:close", "Fermer")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
